import Axios from '../../axiosConfig';
import { BACKEND_URL } from '../../config';
import { configure, makeAutoObservable, runInAction } from 'mobx';
import downloadjs from 'downloadjs';
import '../../styled/froalaLinkStyles.css';
import {
    ChapterDto,
    ChapterWithNumber,
    DocumentDto,
    DocumentFocusAreaTitlesDto,
    DocumentHistoryDto,
    DocumentHistoryItem,
    DocumentShareDto,
    DocumentStatus,
    DocumentTypeDto,
    DownloadPowerPointParams,
    EvaDecisionOptionDto,
    EvaImpactOnTargetDto,
    EvaPerspectiveDto,
    FindDocumentsRequest,
    FocusAreaDto,
    GoalActionDto,
    GoalDto,
    ListDocumentsForOrganizationsRequest,
    ListHistoryByDocumentAndDateRequest,
    ListOfficialDocumentsResponse,
    NewDocumentRequest,
    ShareDocumentPdfRequest,
    UpdateDocumentChaptersRequest,
    UpdateDocumentFocusAreaChapterRequest,
} from '../../types/document';
import {
    AddEvaImpactOnTargetRequest,
    ChapterIndicatorPackageDto,
    ChapterIndicatorThemeDto,
    IndicatorDto,
    UpdateDocumentIndicatorAreaChapterRequest,
    UpdateEvaDecisionOptionAreaRequest,
} from '../../types/indicator';
import { FroalaConfigObject } from '../../types/froalaConfig';
import IndicatorPrintOption from '../../types/indicatorPrintOption';
import RootModule from '../rootModule';
import { UserDto } from '../../types/user';

configure({ enforceActions: 'observed' });

export default class DocumentModule {
    private rootModule: RootModule;
    private allPublishedDocs: DocumentDto[] = [];
    private latestPublishedDocs: DocumentDto[] = [];
    private docTypes: DocumentTypeDto[] = [];
    private organizationDocs: DocumentDto[] = [];
    private activeDocument: DocumentDto = {} as DocumentDto;
    private currentDocumentHistory: DocumentHistoryDto[] = [];
    private currentDocumentHistoryDates: string[] = [];
    private documentShares: DocumentShareDto[] = [];
    private focusAreaTable = false;
    private isPreviewMode = false;

    private docStatuses: DocumentStatus[] = [];

    constructor(rootModule: RootModule) {
        makeAutoObservable(this, {}, { autoBind: true });
        this.rootModule = rootModule;
    }

    public get isPreview(): boolean {
        return this.isPreviewMode;
    }
    public set isPreview(value: boolean) {
        this.isPreviewMode = value;
    }

    public get isChapterLocked(): boolean {
        if (this.currentDocument && this.currentDocument.chapters) {
            // The current document is set and contains chapters, so check whether any chapter is locked.
            return this.currentDocument.chapters.some((chp) => chp.locked);
        } else {
            // Either the current document is not set, or it has no chapters — therefore, there can't be any locked chapters.
            return false;
        }
    }

    public get currentDocument(): DocumentDto {
        return this.activeDocument;
    }

    public get allPublishedDocuments(): DocumentDto[] {
        return this.allPublishedDocs;
    }

    public get largeReportTermsFromPublishedDocuments(): string[] {
        const termsList: string[] = [];
        this.allPublishedDocuments.forEach((document: DocumentDto) =>
            document.yearBegin &&
            document.yearEnd &&
            !termsList.some((term) => term === document.yearBegin + ' - ' + document.yearEnd)
                ? termsList.push(document.yearBegin + ' - ' + document.yearEnd)
                : null,
        );
        return termsList.sort().reverse();
    }

    public get yearlyReportYearsFromPublishedDocuments(): number[] {
        const yearsList: number[] = [];
        this.allPublishedDocuments.forEach((document: DocumentDto) =>
            document.yearBegin && !yearsList.some((year) => document.yearBegin === year)
                ? yearsList.push(document.yearBegin)
                : null,
        );
        return yearsList.sort().reverse();
    }

    public get focusAreaTableHidden(): boolean {
        return this.focusAreaTable;
    }
    public set focusAreaTableHidden(value: boolean) {
        this.focusAreaTable = value;
    }

    public get froalaFocusAreaConfig(): FroalaConfigObject {
        return {
            attribution: false,
            autofocus: true,
            editable: true,
            fileUpload: false,
            fontFamily: {
                Arial: 'Arial',
                Georgia: 'Georgia',
                Impact: 'Impact',
                Tahoma: 'Tahoma',
                'Times New Roman': 'Times New Roman',
                Verdana: 'Verdana',
                'Work Sans': 'Work Sans',
                'Work Sans Medium': 'Work Sans Medium',
            },
            fontFamilySelection: true,
            heightMax: 400,
            heightMin: 200,
            imagePaste: false,
            imageUpload: false,
            key: 'Ja2A4wB1B1E1E2G4D3I4oCd2ZSb1XHi1Cb2a1KIWCWMJHXCLSwG1G1B2A2B1D7C4E1F4B3==',
            language: this.rootModule.localization.locale === 'fi_FI' ? 'fi' : 'sv',
            linkAlwaysBlank: true,
            linkConvertEmailAddress: false,
            linkEditButtons: ['linkEdit', 'linkRemove', 'linkStyle'],
            linkInsertButtons: ['linkBack'],
            linkStyles: {
                noUnderline: this.rootModule.localization.locale === 'fi_FI' ? 'ei alleviivausta' : 'no underline',
                blackColor: this.rootModule.localization.locale === 'fi_FI' ? 'väri: musta' : 'color: black',
            },
            pastePlain: true,
            placeholderText: '',
            spellcheck: false,
            toolbarButtons: [
                'fullscreen',
                'bold',
                'italic',
                'textColor',
                'underline',
                'paragraphFormat',
                'strikeThrough',
                'fontFamily',
                'fontSize',
                'align',
                'outdent',
                'indent',
                'formatOL',
                'formatUL',
                'insertTable',
                'undo',
                'redo',
                'clearFormatting',
                'html',
                'insertLink',
                'selectAll',
            ],
            quickInsertButtons: ['table', 'ul', 'ol', 'hr'],
            toolbarButtonsMD: null,
            toolbarButtonsSM: null,
            toolbarButtonsXS: null,
            requestWithCredentials: true,
        };
    }

    public get froalaConfig(): FroalaConfigObject {
        return {
            attribution: false,
            autofocus: true,
            editable: true,
            fileInsertButtons: ['fileInsert'],
            fileUploadURL: BACKEND_URL + 'documentAttachment/addChapterAttachment',
            events: {
                /*If file is inserted via froala text editor's 'Insert file' toolbar button, link does not open to new tab
                 * when it is clicked. Therefore, attribute target=_blank is added manually to link elements here.
                 * */
                'file.inserted': function ($file: any) {
                    $file.attr('target', '_blank');
                    $file.attr('rel', 'noreferrer noopener');
                },
            },
            fontFamily: {
                Arial: 'Arial',
                Georgia: 'Georgia',
                Impact: 'Impact',
                Tahoma: 'Tahoma',
                'Times New Roman': 'Times New Roman',
                Verdana: 'Verdana',
                'Work Sans': 'Work Sans',
                'Work Sans Medium': 'Work Sans Medium',
            },
            fontFamilySelection: true,
            heightMax: 400,
            heightMin: 200,
            imageInsertButtons: ['imageUpload'],
            imageEditButtons: ['imageAlign', 'imageDisplay', 'imageSize', 'imageAlt', 'imageReplace', 'imageRemove'],
            imagePasteProcess: true,
            imageUploadURL: BACKEND_URL + 'documentAttachment/addChapterImage',
            key: 'Ja2A4wB1B1E1E2G4D3I4oCd2ZSb1XHi1Cb2a1KIWCWMJHXCLSwG1G1B2A2B1D7C4E1F4B3==',
            language: this.rootModule.localization.locale === 'fi_FI' ? 'fi' : 'sv',
            linkAlwaysBlank: true,
            linkConvertEmailAddress: false,
            linkEditButtons: ['linkEdit', 'linkRemove', 'linkStyle'],
            linkInsertButtons: ['linkBack'],
            linkStyles: {
                noUnderline: this.rootModule.localization.locale === 'fi_FI' ? 'ei alleviivausta' : 'no underline',
                blackColor: this.rootModule.localization.locale === 'fi_FI' ? 'väri: musta' : 'color: black',
            },
            pastePlain: true,
            placeholderText: '',
            spellcheck: false,
            toolbarButtons: [
                'fullscreen',
                'bold',
                'italic',
                'textColor',
                'underline',
                'paragraphFormat',
                'strikeThrough',
                'fontFamily',
                'fontSize',
                'align',
                'outdent',
                'indent',
                'formatOL',
                'formatUL',
                'insertTable',
                'undo',
                'redo',
                'clearFormatting',
                'html',
                'insertImage',
                'insertLink',
                'insertFile',
                'selectAll',
                '-',
            ],
            quickInsertButtons: ['image', 'table', 'ul', 'ol', 'hr'],
            toolbarButtonsMD: null,
            toolbarButtonsSM: null,
            toolbarButtonsXS: null,
            requestWithCredentials: true,
        };
    }

    public async downloadEvaDecisionOptionCompareTablePDF(): Promise<void> {
        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + `document/${this.currentDocument.id}/documentExport/downloadEvaCompareTablePdf`,
            data: { id: this.currentDocument.id },
            responseType: 'blob',
        });
        const blob = new Blob([response.data], { type: 'application/pdf' });
        downloadjs(blob, `${this.currentDocument.name}.pdf`, 'application/pdf');
    }

    public async downloadEvaDecisionOptionCompareTableExcel(): Promise<void> {
        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + `document/${this.currentDocument.id}/documentExport/downloadEvaCompareTableExcel`,
            data: { id: this.currentDocument.id },
            responseType: 'blob',
        });
        const blob = new Blob([response.data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        downloadjs(
            blob,
            `${this.currentDocument.name}.xlsx`,
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        );
    }

    public async createGraphImage(svg: string, indicatorName: string): Promise<void> {
        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + `document/${this.currentDocument.id}/documentExport/downloadGraphImage`,
            data: { value: svg },
            responseType: 'blob',
        });

        downloadjs(response.data, `${indicatorName}.png`, 'image/png');
    }

    public async addFocusArea(chapterId: number, focusArea: FocusAreaDto): Promise<void> {
        const documentId = this.currentDocument.id;
        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + `document/${documentId}/chapter/${chapterId}/chapterFocusArea/addFocusArea`,
            data: focusArea,
            withCredentials: true,
        });
        runInAction(() => {
            this.currentDocument.chapters
                .find((chapter: ChapterDto) => chapter.isFocusArea)!
                .focusAreas.push(response.data);
        });
    }

    public async saveFocusArea(focusAreaToBeSaved: FocusAreaDto): Promise<void> {
        const documentId = this.currentDocument.id;
        const focusAreaChapter = this.currentDocument.chapters.find((chapter: ChapterDto) => chapter.isFocusArea);

        const response = await Axios({
            method: 'POST',
            url:
                BACKEND_URL + `document/${documentId}/chapter/${focusAreaChapter?.id}/chapterFocusArea/updateFocusArea`,
            data: focusAreaToBeSaved,
            withCredentials: true,
        });

        if (response.status === 200) {
            if (focusAreaChapter) {
                runInAction(() => {
                    focusAreaChapter.focusAreas = focusAreaChapter.focusAreas.map((focusArea: FocusAreaDto) => {
                        if (focusArea.id === focusAreaToBeSaved.id) {
                            return response.data;
                        }
                        return focusArea;
                    });
                });
            }
        }
    }

    public async removeFocusArea(focusAreaId: number): Promise<void> {
        const documentId = this.currentDocument.id;
        const focusAreaChapter = this.currentDocument.chapters.find((chapter: ChapterDto) => chapter.isFocusArea);

        const response = await Axios({
            method: 'DELETE',
            url:
                BACKEND_URL +
                `document/${documentId}/chapter/${focusAreaChapter?.id}/chapterFocusArea/removeFocusArea/${focusAreaId}`,
            withCredentials: true,
        });
        runInAction(() => {
            if (response.status === 200) {
                focusAreaChapter!.focusAreas = focusAreaChapter!.focusAreas.filter(
                    (focusArea: FocusAreaDto) => focusArea.id !== focusAreaId,
                );
            }
        });
    }

    public async updateFocusAreaTableTitles(titles: DocumentFocusAreaTitlesDto): Promise<void> {
        const documentId = this.currentDocument.id;
        let chapterId = this.currentDocument.chapters.find((chapter: ChapterDto) => chapter.isFocusArea)?.id;
        if (!chapterId) chapterId = this.currentDocument.chapters[0]?.id;
        const response = await Axios({
            method: 'POST',
            url:
                BACKEND_URL + `document/${documentId}/chapter/${chapterId}/chapterFocusArea/updateFocusAreaTableTitles`,
            data: titles,
            withCredentials: true,
        });
        runInAction(() => {
            if (response.status === 200) {
                this.currentDocument.focusAreaTableTitles = response.data;
            }
        });
    }

    public async addGoal(goal: GoalDto): Promise<void> {
        const documentId = this.currentDocument.id;
        const focusAreaChapter = this.currentDocument.chapters.find((chapter: ChapterDto) => chapter.isFocusArea)!;
        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + `document/${documentId}/chapter/${focusAreaChapter.id}/chapterFocusArea/addGoal`,
            data: goal,
            withCredentials: true,
        });
        runInAction(() => {
            this.currentDocument.chapters
                .find((chapter: ChapterDto) => chapter.isFocusArea)!
                .focusAreas.find((focusArea: FocusAreaDto) => focusArea.id === goal.focusAreaId)!
                .goals.push(response.data);
        });
    }

    public async saveGoal(goal: GoalDto): Promise<void> {
        const focusAreaChapter = this.currentDocument.chapters.find((chapter: ChapterDto) => chapter.isFocusArea);
        const response = await Axios({
            method: 'POST',
            url:
                BACKEND_URL +
                `document/${this.currentDocument.id}/chapter/${focusAreaChapter?.id}/chapterFocusArea/updateGoal`,
            data: goal,
            withCredentials: true,
        });
        if (response.status === 200) {
            const focusArea = focusAreaChapter?.focusAreas.find(
                (focusArea: FocusAreaDto) => focusArea.id === goal.focusAreaId,
            );
            if (focusArea) {
                runInAction(() => {
                    focusArea.goals = focusArea.goals.map((goalOfFocusArea: GoalDto) => {
                        if (goalOfFocusArea.id === goal.id) {
                            return response.data as GoalDto;
                        } else {
                            return goalOfFocusArea;
                        }
                    });
                });
            }
        }
    }

    public async removeGoal(focusAreaId: number, goalId: number): Promise<void> {
        const focusAreaChapter = this.currentDocument.chapters.find((chapter: ChapterDto) => chapter.isFocusArea);
        const response = await Axios({
            method: 'DELETE',
            url:
                BACKEND_URL +
                `document/${this.currentDocument.id}/chapter/${focusAreaChapter?.id}/chapterFocusArea/removeGoal/${goalId}`,
            withCredentials: true,
        });
        if (response.status === 204) {
            const focusArea = focusAreaChapter?.focusAreas.find(
                (focusArea: FocusAreaDto) => focusArea.id === focusAreaId,
            );
            if (focusArea) {
                runInAction(() => {
                    focusArea.goals = focusArea.goals.filter((goal: GoalDto) => goal.id !== goalId);
                });
            }
        }
    }

    public async addGoalAction(focusAreaId: number, goalAction: GoalActionDto): Promise<GoalActionDto> {
        const documentId = this.currentDocument.id;
        const focusAreaChapter = this.currentDocument.chapters.find((chapter: ChapterDto) => chapter.isFocusArea)!;
        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + `document/${documentId}/chapter/${focusAreaChapter.id}/chapterFocusArea/addGoalAction`,
            data: goalAction,
            withCredentials: true,
        });
        runInAction(() => {
            this.currentDocument.chapters
                .find((chapter: ChapterDto) => chapter.isFocusArea)!
                .focusAreas.find((focusArea: FocusAreaDto) => focusArea.id === focusAreaId)!
                .goals.find((goal: GoalDto) => goal.id === goalAction.goalId)!
                .actions.push(response.data);
        });
        return response.data;
    }

    public async saveGoalAction(
        focusAreaId: number,
        goalId: number,
        goalAction: GoalActionDto,
    ): Promise<GoalActionDto> {
        const documentId = this.currentDocument.id;
        const focusAreaChapter = this.currentDocument.chapters.find((chapter: ChapterDto) => chapter.isFocusArea);

        const response = await Axios({
            method: 'POST',
            url:
                BACKEND_URL +
                `document/${documentId}/chapter/${focusAreaChapter?.id}/chapterFocusArea/updateGoalAction`,
            data: goalAction,
            withCredentials: true,
        });
        if (response.status === 200) {
            const focusArea = focusAreaChapter?.focusAreas.find((area: FocusAreaDto) => area.id === focusAreaId);
            const focusAreaGoal = focusArea?.goals.find((goal: GoalDto) => goal.id === goalId);
            if (focusAreaGoal) {
                runInAction(() => {
                    focusAreaGoal.actions = focusAreaGoal.actions.map((action: GoalActionDto) => {
                        if (action.id === goalAction.id) {
                            return response.data as GoalActionDto;
                        }
                        return action;
                    });
                });
            }
        }
        return response.data;
    }

    public async removeGoalAction(focusAreaId: number, goalId: number, goalActionId: number): Promise<void> {
        const focusAreaChapter = this.currentDocument.chapters.find((chapter: ChapterDto) => chapter.isFocusArea);
        const response = await Axios({
            method: 'DELETE',
            url:
                BACKEND_URL +
                `document/${this.currentDocument.id}/chapter/${focusAreaChapter?.id}/chapterFocusArea/removeGoalAction/${goalActionId}`,
            withCredentials: true,
        });
        if (response.status === 204) {
            const focusArea = focusAreaChapter?.focusAreas.find((area: FocusAreaDto) => area.id === focusAreaId);
            const focusAreaGoal = focusArea?.goals.find((goal: GoalDto) => goal.id === goalId);
            if (focusAreaGoal) {
                runInAction(() => {
                    focusAreaGoal.actions = focusAreaGoal.actions.filter(
                        (action: GoalActionDto) => action.id !== goalActionId,
                    );
                });
            }
        }
    }

    public changeActiveDocumentName(name: string): void {
        this.activeDocument.name = name;
    }

    public changeActiveDocumentCoverPageText(text: string | null): void {
        this.activeDocument.coverPageText = text;
    }

    public async saveEvaDecisionOption(evaDecisionOption: EvaDecisionOptionDto): Promise<void> {
        const documentId = this.currentDocument.id;
        const evaDecisionOptionChapterId = this.evaDecisionOptionChapter!.id;

        const response = await Axios({
            method: 'PUT',
            url:
                BACKEND_URL +
                `document/${documentId}/chapter/${evaDecisionOptionChapterId}/chapterEva/updateEvaDecision`,
            data: evaDecisionOption,
            withCredentials: true,
        });
        await runInAction(async () => {
            this.evaDecisionOptionChapter!.evaDecisionOptions = this.evaDecisionOptionChapter!.evaDecisionOptions.map(
                (d) => (d.id === response.data.id ? (response.data as EvaDecisionOptionDto) : d),
            );
            await this.reloadCurrentDocument();
        });
    }

    public async addEvaDecisionOption(evaDecisionOption: EvaDecisionOptionDto): Promise<void> {
        const documentId = this.currentDocument.id;
        const evaDecisionOptionChapterId = this.evaDecisionOptionChapter!.id;
        const response = await Axios({
            method: 'POST',
            url:
                BACKEND_URL +
                `document/${documentId}/chapter/${evaDecisionOptionChapterId}/chapterEva/addEvaDecisionOption`,
            data: evaDecisionOption,
            withCredentials: true,
        });
        await runInAction(async () => {
            this.evaDecisionOptionChapter?.evaDecisionOptions.push(response.data);
            await this.reloadCurrentDocument();
        });
    }

    public async removeEvaDecisionOption(
        chapterId: number,
        documentId: number,
        decisionOptionId: number,
    ): Promise<void> {
        await Axios({
            method: 'DELETE',
            url:
                BACKEND_URL +
                `document/${documentId}/chapter/${chapterId}/chapterEva/removeEvaDecision/${decisionOptionId}`,
            withCredentials: true,
        });
        this.removeEvaDecisionOptionFromList(decisionOptionId);
        await this.reloadCurrentDocument();
    }

    private removeEvaDecisionOptionFromList(id: number): void {
        if (this.evaDecisionOptionChapter?.evaDecisionOptions) {
            this.evaDecisionOptionChapter.evaDecisionOptions = this.evaDecisionOptionChapter?.evaDecisionOptions.filter(
                (decisionOption) => decisionOption.id !== id,
            );
        }
    }

    public async removeEvaPerspective(perspective: EvaPerspectiveDto, chapter: ChapterDto): Promise<void> {
        const title = perspective.title;
        const perspectiveId = perspective.id;
        await Axios({
            method: 'DELETE',
            url:
                BACKEND_URL +
                `document/${chapter.documentId}/chapter/${chapter.id}/chapterEva/removeEvaPerspective/${perspectiveId}`,
            withCredentials: true,
        });
        runInAction(() => {
            chapter.evaDecisionOptions.forEach(
                (option: EvaDecisionOptionDto) =>
                    (option.evaPerspectives = option.evaPerspectives.filter(
                        (perspective: EvaPerspectiveDto) => perspective.title !== title,
                    )),
            );
        });
    }

    public async addEvaPerspective(title: string): Promise<void> {
        const chapter = this.currentDocument.chapters.find((chapter: ChapterDto) => chapter.isEvaDecisionOptionArea)!;
        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + `document/${chapter.documentId}/chapter/${chapter.id}/chapterEva/addEvaPerspective`,
            data: { value: title },
            withCredentials: true,
        });
        runInAction(() => {
            const responseData: EvaPerspectiveDto[] = response.data;

            responseData.forEach((d: EvaPerspectiveDto) => {
                const decisionOptionOfPerspective = chapter.evaDecisionOptions.find(
                    (option) => option.id === d.optionId,
                );
                if (
                    decisionOptionOfPerspective &&
                    !decisionOptionOfPerspective.evaPerspectives.some((perspective) => perspective.id === d.id)
                ) {
                    decisionOptionOfPerspective.evaPerspectives.push(d);
                }
            });
        });
    }

    public async saveEvaPerspective(evaPerspective: EvaPerspectiveDto): Promise<void> {
        const chapter = this.currentDocument.chapters.find((chapter: ChapterDto) => chapter.isEvaDecisionOptionArea)!;
        await Axios({
            method: 'PUT',
            url: BACKEND_URL + `document/${chapter.documentId}/chapter/${chapter.id}/chapterEva/updateEvaPerspective`,
            data: evaPerspective,
            withCredentials: true,
        });
    }

    public async createDocument(params: NewDocumentRequest): Promise<void> {
        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + 'document/createDocument',
            data: params,
            withCredentials: true,
        });
        this.setCurrentDocument(response.data);
    }

    public async updateDocumentChapters(requestData: UpdateDocumentChaptersRequest): Promise<void> {
        await Axios({
            method: 'POST',
            url: BACKEND_URL + 'document/updateDocumentChapters',
            data: requestData,
            withCredentials: true,
        });
        await runInAction(() => this.refreshCurrentDocument());
    }

    public hasSubChapters(chapterId: number): boolean {
        const foundChapter = this.currentDocument.chapters.find((chp) => chp.id === chapterId);
        if (foundChapter) {
            const currentChapterIndex = this.currentDocument.chapters.indexOf(foundChapter);
            // If the next chapter from current has higher level, then current chapter has subchapters.
            return foundChapter.chapterLevel < this.currentDocument.chapters[currentChapterIndex + 1]?.chapterLevel;
        }
        return false;
    }

    // Set the direct flag to true if only "first" level of subchapters are needed.
    public findSubChapters(chapterId: number, direct?: boolean): ChapterDto[] {
        const chapters = this.currentDocument.chapters;
        const foundChapter = chapters.find((chp) => chp.id === chapterId);
        const subChapters: ChapterDto[] = [];

        if (!foundChapter) return subChapters;

        const currentChapterIndex = chapters.indexOf(foundChapter);
        const nextChapters = chapters.slice(currentChapterIndex + 1, chapters.length);

        if (!nextChapters.length) return subChapters;

        if (direct) {
            // If the iterated chapter's level - 1 === found chapter's level,
            // it is a direct subchapter of the found chapter.
            for (let i = 0; i < nextChapters.length; i++) {
                if (nextChapters[i]?.chapterLevel - 1 === foundChapter.chapterLevel) {
                    subChapters.push(nextChapters[i]);
                } else if (nextChapters[i]?.chapterLevel === foundChapter.chapterLevel) {
                    // Break the loop if the iterated chapter's level is the same as found chapter's level.
                    return subChapters;
                }
            }
        } else {
            // If the iterated chapter's level is greater than the found chapter level,
            // it is a subchapter or a nested subchapter of the found chapter.
            for (let i = 0; i < nextChapters.length; i++) {
                if (nextChapters[i]?.chapterLevel > foundChapter.chapterLevel) {
                    subChapters.push(nextChapters[i]);
                } else {
                    return subChapters;
                }
            }
        }
        return subChapters;
    }

    public async resetIndicatorModel(): Promise<void> {
        const response = await Axios({
            method: 'PUT',
            url: BACKEND_URL + `document/resetDocumentIndicatorModel/${this.currentDocument.id}`,
            withCredentials: true,
        });
        if (response.status === 200)
            await runInAction(() => this.setIndicatorArea(response.data as ChapterIndicatorPackageDto[]));
    }

    public async updateDocumentIndicatorAreaChapter(
        requestData: UpdateDocumentIndicatorAreaChapterRequest,
    ): Promise<void> {
        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + 'document/updateDocumentIndicatorAreaChapter',
            data: requestData,
            withCredentials: true,
        });
        if (response.status === 200) await runInAction(() => this.setIndicatorArea(response.data));
    }

    public async setIndicatorArea(indicatorPackages: ChapterIndicatorPackageDto[]): Promise<void> {
        await Promise.all(
            this.activeDocument.chapters.map((chapter: ChapterDto) => {
                if (chapter.isIndicatorArea) chapter.indicatorPackages = indicatorPackages;
                return chapter;
            }),
        );
    }

    public async updateDocumentFocusAreaChapter(requestData: UpdateDocumentFocusAreaChapterRequest): Promise<void> {
        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + 'document/updateDocumentFocusAreaChapter',
            data: requestData,
            withCredentials: true,
        });
        runInAction(() => {
            if (response.status === 200)
                this.currentDocument.chapters.find((chapter: ChapterDto) => chapter.isFocusArea)!.focusAreas =
                    response.data as FocusAreaDto[];
        });
    }

    public async updateDocumentEvaDecisionAreaChapter(requestData: UpdateEvaDecisionOptionAreaRequest): Promise<void> {
        const documentId = this.currentDocument.id;
        const chapterId = this.currentDocument.chapters.find(
            (chapter: ChapterDto) => chapter.isEvaDecisionOptionArea,
        )!.id;
        const response = await Axios({
            method: 'PUT',
            url: BACKEND_URL + `document/${documentId}/chapter/${chapterId}/chapterEva/updateEvaDecisionOptionArea`,
            data: requestData,
            withCredentials: true,
        });
        runInAction(() => {
            if (response.status === 200)
                this.currentDocument.chapters.find(
                    (chapter: ChapterDto) => chapter.isEvaDecisionOptionArea,
                )!.evaDecisionOptions = response.data as EvaDecisionOptionDto[];
        });
    }

    public async fetchAvailableDocumentTypes(): Promise<void> {
        const response = await Axios({
            method: 'GET',
            url: BACKEND_URL + 'document/availableDocumentTypes',
            withCredentials: true,
        });
        if (response.status === 200) runInAction(() => (this.docTypes = response.data));
    }

    public async fetchAvailableDocumentStatuses(): Promise<void> {
        const response = await Axios({
            method: 'GET',
            url: BACKEND_URL + 'document/availableDocumentStatuses',
            withCredentials: true,
        });
        if (response.status === 200) runInAction(() => (this.docStatuses = response.data));
    }

    public async chaptersStatus(): Promise<void> {
        if (this.activeDocument.id) {
            const response = await Axios({
                method: 'GET',
                url: BACKEND_URL + `chapter/chaptersStatus/${this.activeDocument.id}`,
                withCredentials: true,
            });
            if (response.status === 200) {
                response.data.forEach((c: ChapterDto) => {
                    let chapterHasChanged = false;
                    const chapter = this.activeDocument.chapters.find((chapter) => chapter.id === c.id);
                    if (chapter) {
                        if (chapter.version !== c.version) {
                            chapterHasChanged = true;
                        }

                        if (chapter.isIndicatorArea) {
                            chapter.indicatorPackages.forEach((indicatorPackage) => {
                                const pkg = c.indicatorPackages.find((pkg: any) => pkg.id === indicatorPackage.id);
                                if (pkg) {
                                    if (pkg.version !== indicatorPackage.version) {
                                        chapterHasChanged = true;
                                    }
                                } else {
                                    chapterHasChanged = true;
                                }
                            });
                        }

                        if (chapter.isEvaDecisionOptionArea) {
                            chapter.evaDecisionOptions.forEach((evaDecisionOption) => {
                                const option = c.evaDecisionOptions.find(
                                    (option: EvaDecisionOptionDto) => option.id === evaDecisionOption.id,
                                );
                                if (option) {
                                    if (option.version !== evaDecisionOption.version) {
                                        chapterHasChanged = true;
                                    }
                                } else {
                                    chapterHasChanged = true;
                                }
                            });
                        }

                        if (chapterHasChanged) {
                            // If content in chapter has changed, reload the chapter
                            this.getChapter(chapter.id!);
                        }
                    } else {
                        this.reloadCurrentDocument();
                    }
                });

                runInAction(() => {
                    this.activeDocument.chapters = this.activeDocument.chapters.filter((chapter) =>
                        response.data.some((c: ChapterDto) => c.id === chapter.id),
                    );
                });
            }
        }
    }

    public async getChapter(chapterId: number): Promise<void> {
        const response = await Axios({
            method: 'GET',
            url: BACKEND_URL + `chapter/getChapter/${chapterId}`,
            withCredentials: true,
        });
        if (this.activeDocument.chapters.some((chapter) => chapter.id === response.data.id)) {
            runInAction(() => {
                this.updateChapterData(response.data as ChapterDto);
            });
        } else {
            runInAction(() => {
                this.activeDocument.chapters.push(response.data as ChapterDto);
            });
        }
    }

    public async getIndicatorPackage(indPackage: ChapterIndicatorPackageDto): Promise<void> {
        const documentId = this.activeDocument.id;

        const response = await Axios({
            method: 'GET',
            url:
                BACKEND_URL +
                `document/${documentId}/chapter/${indPackage.chapterId}/chapterIndicator/getIndicatorPackage/${indPackage.id}`,
            withCredentials: true,
        });
        const indicatorPackageFromResponse = response.data as ChapterIndicatorPackageDto;
        if (response.status === 200) {
            runInAction(() => {
                const chapter = this.activeDocument.chapters.find(
                    (c: ChapterDto) => c.id === indicatorPackageFromResponse.chapterId,
                );
                if (chapter) {
                    const indicatorPackage = chapter.indicatorPackages.find(
                        (ind: ChapterIndicatorPackageDto) => ind.id === indicatorPackageFromResponse.id,
                    );
                    if (indicatorPackage) {
                        indicatorPackage.notes = indicatorPackageFromResponse.notes;
                        indicatorPackage.name = indicatorPackageFromResponse.name;
                        indicatorPackage.editor = indicatorPackageFromResponse.editor;
                        indicatorPackage.lastUpdated = indicatorPackageFromResponse.lastUpdated;
                        indicatorPackage.version = indicatorPackageFromResponse.version;
                    }
                }
            });
        }
    }

    public async removeCurrentDocument(): Promise<void> {
        await Axios({
            method: 'DELETE',
            url: BACKEND_URL + `document/removeDocument/${this.activeDocument.id}`,
            withCredentials: true,
        });
        runInAction(() => {
            this.organizationDocs = this.organizationDocs.filter((doc) => doc.id !== this.activeDocument.id);
        });
    }

    public async saveCurrentDocument(): Promise<void> {
        await Axios({
            method: 'POST',
            url: BACKEND_URL + 'document/saveDocument',
            data: this.currentDocument,
            withCredentials: true,
        });
        runInAction(() => {
            const doc = this.organizationDocs.find((d) => d.id === this.currentDocument.id);
            if (doc) {
                doc.name = this.currentDocument.name;
            }
        });
    }

    public async fetchPublishedDocuments(): Promise<void> {
        const response = await Axios({
            method: 'GET',
            url: BACKEND_URL + 'document/listPublishedDocuments',
            withCredentials: true,
        });
        if (response?.status === 200) {
            runInAction(() => {
                this.allPublishedDocs = response.data as DocumentDto[];
                // Filter documents that have acceptedDate defined
                const filtered = this.allPublishedDocs.filter((doc: DocumentDto) => doc.acceptedDate);
                // Latest first
                this.latestPublishedDocs = filtered.sort((a: DocumentDto, b: DocumentDto) => {
                    return b.acceptedDate! - a.acceptedDate!;
                });
            });
        }
    }

    public async fetchDocuments(requestData: FindDocumentsRequest): Promise<DocumentDto[]> {
        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + 'document/findDocuments',
            data: requestData,
            withCredentials: true,
        });
        return response.data;
    }

    public async fetchDocumentsForOrganizations(
        requestData: ListDocumentsForOrganizationsRequest,
    ): Promise<DocumentDto[]> {
        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + 'document/listDocumentsForOrganizations',
            data: requestData,
            withCredentials: true,
        });
        return response.data;
    }

    public async fetchOrganizationDocuments(organizationId: number): Promise<void> {
        const response = await Axios({
            method: 'GET',
            url: BACKEND_URL + `document/listOrganizationDocuments/${organizationId}`,
            withCredentials: true,
        });
        runInAction(() => (this.organizationDocs = response.data));
    }

    public async fetchHistoryOfDocument(date: string): Promise<void> {
        const requestData: ListHistoryByDocumentAndDateRequest = {
            documentId: this.currentDocument.id!,
            date: date,
        };
        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + 'document/listHistoryByDocumentAndDate',
            data: requestData,
            withCredentials: true,
        });
        runInAction(() => (this.currentDocumentHistory = response.data as DocumentHistoryDto[]));
    }

    public async fetchHistoryDates(): Promise<void> {
        const documentId = this.currentDocument.id!;
        const response = await Axios({
            method: 'GET',
            url: BACKEND_URL + `document/listHistoryDatesOfDocument/${documentId}`,
            withCredentials: true,
        });
        runInAction(() => {
            if (response.data.length > 0) {
                this.currentDocumentHistoryDates = response.data as string[];
            } else {
                this.currentDocumentHistoryDates = [];
                //if there is no history dates then there are no history.
                this.currentDocumentHistory = [];
            }
        });
    }

    public async downloadWord(
        colorSchema: string,
        indicatorGraphMode: string,
        printChapterList: boolean,
        chaptersToPrint: number[],
        indicatorDescription: boolean,
        indicatorsToPrint: IndicatorPrintOption[],
    ): Promise<void> {
        const indicatorPackagesWithGraphs = indicatorsToPrint.filter((pkg) => pkg.printGraph).map((pkg) => pkg.id!);
        const indicatorPackagesWithNotes = indicatorsToPrint.filter((pkg) => pkg.printNote).map((pkg) => pkg.id!);

        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + `document/${this.currentDocument.id}/documentExport/downloadWord`,
            data: {
                id: this.currentDocument.id,
                chaptersToPrint,
                colorSchema,
                hideFocusAreaTable: this.focusAreaTable,
                indicatorDescription,
                indicatorGraphMode,
                indicatorPackagesWithGraphs,
                indicatorPackagesWithNotes,
                printChapterList,
            },
            responseType: 'blob',
        });
        const blob = new Blob([response.data], {
            type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        });
        downloadjs(
            blob,
            `${this.currentDocument.name}.docx`,
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        );
    }

    public async downloadPdf(
        colorSchema: string,
        indicatorGraphMode: string,
        printChapterList: boolean,
        chaptersToPrint: number[],
        indicatorDescription: boolean,
        indicatorsToPrint: IndicatorPrintOption[],
    ): Promise<void> {
        const indicatorPackagesWithGraphs = indicatorsToPrint.filter((pkg) => pkg.printGraph).map((pkg) => pkg.id!);
        const indicatorPackagesWithNotes = indicatorsToPrint.filter((pkg) => pkg.printNote).map((pkg) => pkg.id!);

        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + `document/${this.currentDocument.id}/documentExport/downloadPdf`,
            data: {
                id: this.currentDocument.id,
                chaptersToPrint,
                colorSchema,
                hideFocusAreaTable: this.focusAreaTable,
                indicatorDescription,
                indicatorGraphMode,
                indicatorPackagesWithGraphs,
                indicatorPackagesWithNotes,
                printChapterList,
            },
            responseType: 'blob',
        });
        const blob = new Blob([response.data], { type: 'application/pdf' });
        downloadjs(blob, `${this.currentDocument.name}.pdf`, 'application/pdf');
    }

    public async downloadExcelTemplate(): Promise<void> {
        const response = await Axios({
            method: 'GET',
            url: BACKEND_URL + `document/${this.currentDocument.id}/documentExport/downloadExcelTemplate`,
        });
        downloadjs(
            `data:application/vnd.ms-excel;base64,${response.data}`,
            'Sähköinen hyvinvointikertomus_useamman indikaattorin lisääminen esimerkki.xlsx',
            '.xlsx',
        );
    }

    public async fetchHistoryById(id: number): Promise<DocumentHistoryItem> {
        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + 'document/getHistoryByHistoryId',
            data: {
                id,
            },
        });
        return response.data;
    }

    public async restoreDeletedChapter(requestData: DocumentHistoryDto): Promise<void> {
        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + 'chapter/restoreRemovedChapter',
            data: requestData,
            withCredentials: true,
        });
        await this.chaptersStatus();
        runInAction(() => this.currentDocumentHistory.unshift(response.data as DocumentHistoryDto));
    }

    public async loadDocumentByShareId(shareToken: string): Promise<void> {
        const response = await Axios({
            method: 'GET',
            url: BACKEND_URL + `document/loadSharedById/${shareToken}`,
            withCredentials: true,
        });
        if (response.status === 200) this.setCurrentDocument(response.data);
    }

    public async shareDocumentUrl(shares: DocumentShareDto[]) {
        await Axios({
            method: 'POST',
            url: BACKEND_URL + `document/${this.currentDocument.id}/documentShare/shareDocumentUrl`,
            data: shares,
            withCredentials: true,
        });
    }

    public async shareDocumentPdf(shares: DocumentShareDto[]) {
        const evaDecisionComparison = this.isEvaDocument(this.currentDocument)
            ? this.createEvaDecisionOptionCompareTable
            : [];
        const request: ShareDocumentPdfRequest = {
            shares: shares,
            evaDecisionComparisonTable: evaDecisionComparison,
        };
        await Axios({
            method: 'POST',
            url: BACKEND_URL + `document/${this.currentDocument.id}/documentShare/shareDocumentPdf`,
            data: request,
            withCredentials: true,
        });
    }

    public async removeWorkgroupUser(userId: number): Promise<void> {
        const documentId = this.currentDocument.id;
        await Axios({
            method: 'DELETE',
            url: BACKEND_URL + `document/removeWorkGroupUser/${documentId}/user/${userId}`,
            withCredentials: true,
        });
        runInAction(() => {
            this.activeDocument.users = this.activeDocument.users.filter((user) => user.id !== userId);
        });
    }

    public async addWorkgroupUser(userName: string): Promise<void> {
        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + 'document/addWorkGroupUser',
            data: {
                documentId: this.currentDocument.id,
                username: userName,
            },
            withCredentials: true,
        });
        runInAction(() => {
            this.activeDocument.users.push(response.data);
        });
    }

    public async changeDocumentOwner(newOwner: UserDto): Promise<void> {
        const currentDocumentWithNewOwner = { ...this.currentDocument, documentOwner: newOwner };
        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + 'document/changeDocumentOwner',
            data: currentDocumentWithNewOwner,
            withCredentials: true,
        });
        runInAction(() => {
            this.activeDocument.documentOwner = response.data;
        });
    }

    public async fetchPreviousDocumentShares(): Promise<void> {
        const documentId = this.currentDocument.id;
        const response = await Axios({
            method: 'GET',
            url: BACKEND_URL + `document/${documentId}/documentShare/listDocumentShares`,
            withCredentials: true,
        });
        runInAction(() => (this.documentShares = response.data as DocumentShareDto[]));
    }

    public async removeDocumentShare(id: string): Promise<void> {
        const documentId = this.currentDocument.id;
        await Axios({
            method: 'DELETE',
            url: BACKEND_URL + `document/${documentId}/documentShare/removeDocumentShare/${id}`,
            withCredentials: true,
        });
        const filtered = this.documentShares.filter((share) => share.id !== id);
        runInAction(() => (this.documentShares = filtered));
    }

    public async removeAllDocumentShares(): Promise<void> {
        const documentId = this.currentDocument.id;
        await Axios({
            method: 'DELETE',
            url: BACKEND_URL + `document/${documentId}/documentShare/removeAllDocumentShares`,
            withCredentials: true,
        });
        runInAction(() => (this.documentShares = []));
    }

    public async approveDocument(requestData: DocumentDto): Promise<void> {
        await Axios({
            method: 'POST',
            url: BACKEND_URL + 'document/approveDocument',
            data: requestData,
            withCredentials: true,
        });
        if (!this.isCurrentDocumentEva) {
            await this.changeDocumentIsOfficial(true); // Change document official if it is not already
        }
        await this.fetchOrganizationDocuments(this.currentDocument.organization.id!);
    }

    public async convertEvaMiniToLarge(documentId: number): Promise<void> {
        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + 'document/convertMiniEvaToEva',
            data: { id: documentId },
            withCredentials: true,
        });
        await this.closeCurrentDocument();
        await this.loadDocumentById(response.data as number);
    }

    public async disapproveDocument(): Promise<void> {
        await Axios({
            method: 'PUT',
            url: BACKEND_URL + `document/disapproveDocument/${this.currentDocument.id}`,
            withCredentials: true,
        });
        await this.fetchOrganizationDocuments(this.currentDocument.organization.id!);
    }

    public async uploadCoverPageImage(file: File): Promise<void> {
        const form = new FormData();
        form.append('file', file);
        form.append('id', this.currentDocument.id!.toString());

        await Axios({
            method: 'POST',
            url: BACKEND_URL + 'document/upload',
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            data: form,
        });
    }

    public async reloadCurrentDocument(): Promise<void> {
        if (this.isPreview) {
            await this.loadPublishedDocumentById(this.currentDocument.id!);
        } else {
            await this.loadDocumentById(this.currentDocument.id!);
        }
    }

    public async refreshCurrentDocument(): Promise<void> {
        const documentId = this.currentDocument.id;
        await this.closeCurrentDocument();
        await this.loadDocumentById(documentId!);
    }

    public addCurrentDocumentIndicators(theme: ChapterIndicatorThemeDto): void {
        const pkg = this.indicatorPackageChapter?.indicatorPackages.find((pkg) => pkg.id === theme.pkgId);

        const themeWithUpdatedIndicators = pkg?.themes.find((thm) => thm.id === theme.id);

        theme.indicators.forEach((indicator) => themeWithUpdatedIndicators?.indicators.push(indicator));
    }

    public async updateEvaImpactOnTargetTitle(
        impactOnTarget: EvaImpactOnTargetDto,
        oldTargetTitle: string | null,
    ): Promise<void> {
        const documentId = this.currentDocument.id;
        const chapter = this.currentDocument.chapters.find((chapter) => chapter.isEvaDecisionOptionArea)!;
        const response = await Axios({
            method: 'PUT',
            url: BACKEND_URL + `document/${documentId}/chapter/${chapter.id}/chapterEva/updateEvaImpactOnTargetTitle`,
            data: impactOnTarget,
            withCredentials: true,
        });

        if (response.status === 204) {
            const perspectives = chapter.evaDecisionOptions.map((option) => option.evaPerspectives).flat();

            const perspectiveOfImpactOnTarget = perspectives.find((p) => p.id === impactOnTarget.evaPerspectiveId);

            chapter.evaDecisionOptions.forEach((option) => {
                option.evaPerspectives.forEach((perspective) => {
                    if (perspective.title === perspectiveOfImpactOnTarget?.title) {
                        perspective.impactTargets.forEach((impact) => {
                            if (impact.target === oldTargetTitle) {
                                runInAction(() => {
                                    impact.target = impactOnTarget.target;
                                });
                            }
                        });
                    }
                });
            });
        }
    }

    public removeCurrentDocumentImpactOnTarget(params: EvaImpactOnTargetDto): void {
        const decisionOptions = this.evaDecisionOptionChapter?.evaDecisionOptions;
        const perspectives = decisionOptions?.map((option) => option.evaPerspectives).flat();
        const perspectiveOfImpactTarget = perspectives?.find(
            (perspective) => perspective.id === params.evaPerspectiveId,
        );

        decisionOptions?.forEach((decisionOption) => {
            decisionOption.evaPerspectives.forEach((perspective) => {
                if (perspective.title === perspectiveOfImpactTarget?.title) {
                    perspective.impactTargets = perspective.impactTargets.filter(
                        (impactOnTarget) => impactOnTarget.target !== params.target,
                    );
                }
            });
        });
    }

    public async addEvaImpactOnTarget(params: AddEvaImpactOnTargetRequest): Promise<void> {
        const documentId = this.currentDocument.id;
        const evaDecisionOptionArea = this.currentDocument.chapters.find((chapter) => chapter.isEvaDecisionOptionArea)!;
        const response = await Axios({
            method: 'POST',
            url:
                BACKEND_URL +
                `document/${documentId}/chapter/${evaDecisionOptionArea.id}/chapterEva/addEvaImpactOnTarget`,
            data: params,
            withCredentials: true,
        });

        if (response.status === 200) {
            const newAddedImpactTargets: EvaImpactOnTargetDto[] = response.data;
            const evaDecisionOptionArea = this.currentDocument.chapters.find(
                (chapter) => chapter.isEvaDecisionOptionArea,
            );

            newAddedImpactTargets.forEach((newImpactTarget) => {
                evaDecisionOptionArea?.evaDecisionOptions.forEach((option) => {
                    option.evaPerspectives.forEach((perspective) => {
                        if (perspective.id === newImpactTarget.evaPerspectiveId) {
                            runInAction(() => {
                                perspective.impactTargets.push(newImpactTarget);
                            });
                        }
                    });
                });
            });
        }
    }

    public async saveEvaImpactOnTarget(impact: EvaImpactOnTargetDto): Promise<void> {
        const documentId = this.currentDocument.id;
        const chapterId = this.currentDocument.chapters.find((chapter) => chapter.isEvaDecisionOptionArea)?.id;
        await Axios({
            method: 'PUT',
            url: BACKEND_URL + `document/${documentId}/chapter/${chapterId}/chapterEva/updateEvaImpactOnTarget`,
            data: impact,
            withCredentials: true,
        });
    }

    public async removeEvaImpactOnTarget(impact: EvaImpactOnTargetDto): Promise<void> {
        const documentId = this.currentDocument.id;
        const chapterId = this.currentDocument.chapters.find((chapter) => chapter.isEvaDecisionOptionArea)!.id;
        await Axios({
            method: 'DELETE',
            url:
                BACKEND_URL +
                `document/${documentId}/chapter/${chapterId}/chapterEva/removeEvaImpactOnTarget/${impact.id}`,
            withCredentials: true,
        });
        this.removeCurrentDocumentImpactOnTarget(impact);
    }

    public removeCurrentDocumentImpactOnTargetIndicator(
        impactOnTarget: EvaImpactOnTargetDto,
        indicatorToBeRemoved: IndicatorDto,
    ): void {
        const decisionOptions = this.evaDecisionOptionChapter?.evaDecisionOptions;
        const perspectives = decisionOptions?.map((option) => option.evaPerspectives).flat();
        const perspectiveOfImpactOnTarget = perspectives?.find((p) => p.id === impactOnTarget.evaPerspectiveId);

        perspectives?.forEach((perspective) => {
            if (perspective.title === perspectiveOfImpactOnTarget?.title) {
                perspective.impactTargets.forEach((impact) => {
                    if (impact.target === impactOnTarget.target) {
                        if (indicatorToBeRemoved.sourceCode === 'SOTKANET') {
                            impact.sotkanetIndicators = impact.sotkanetIndicators.filter(
                                (indicator) => indicator.id !== indicatorToBeRemoved.id,
                            );
                        } else if (indicatorToBeRemoved.sourceCode === 'CUSTOM') {
                            impact.organizationIndicators = impact.organizationIndicators.filter(
                                (indicator) => indicator.id !== indicatorToBeRemoved.id,
                            );
                        }
                    }
                });
            }
        });
    }

    public addCurrentDocumentImpactOnTargetIndicators(impactOnTargetWithNewIndicators: EvaImpactOnTargetDto): void {
        const decisionOptions = this.evaDecisionOptionChapter?.evaDecisionOptions;
        const perspectives = decisionOptions?.map((option) => option.evaPerspectives).flat();
        const perspectiveOfTarget = perspectives?.find(
            (perspective) => perspective.id === impactOnTargetWithNewIndicators.evaPerspectiveId,
        );
        decisionOptions?.forEach((decisionOption) => {
            decisionOption.evaPerspectives.forEach((perspective) => {
                if (perspective.title === perspectiveOfTarget?.title) {
                    perspective.impactTargets.forEach((impactOnTarget) => {
                        if (impactOnTarget.target === impactOnTargetWithNewIndicators.target) {
                            impactOnTargetWithNewIndicators.sotkanetIndicators.forEach((indicator) =>
                                impactOnTarget.sotkanetIndicators.push(indicator),
                            );
                            impactOnTargetWithNewIndicators.organizationIndicators.forEach((indicator) =>
                                impactOnTarget.organizationIndicators.push(indicator),
                            );
                        }
                    });
                }
            });
        });
    }

    public removeCurrentDocumentIndicator(removedIndicator: IndicatorDto): void {
        this.indicatorPackageChapter?.indicatorPackages.forEach((indicatorPackage) => {
            indicatorPackage.themes.forEach((theme) => {
                theme.indicators = theme.indicators.filter((indicator) => indicator.id !== removedIndicator.id);
            });
        });
    }

    public async lockChapter(chapterId: number): Promise<void> {
        const response = await Axios({
            method: 'PUT',
            url: BACKEND_URL + `chapter/lockChapter/${chapterId}`,
            withCredentials: true,
        });
        if (response.status === 200) {
            this.lockChapterById(response.data.id);
            this.updateChapterData(response.data as ChapterDto);
        }
    }

    public async lockEveryChapter(): Promise<void> {
        const documentId = this.currentDocument.id;
        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + 'chapter/lockAllChapters',
            data: { id: documentId },
            withCredentials: true,
        });
        if (response.status === 200) {
            runInAction(() => {
                response.data.forEach((chapter: ChapterDto) => {
                    this.lockChapterById(chapter.id!);
                    this.updateChapterData(chapter);
                });
            });
        }
    }

    public async unlockChapter(chapterId: number): Promise<void> {
        const response = await Axios({
            method: 'PUT',
            url: BACKEND_URL + `chapter/unlockChapter/${chapterId}`,
            withCredentials: true,
        });
        const chapterIdFromResponse = response.data as number;
        this.unlockChapterById(chapterIdFromResponse);
    }

    public async lockFocusAreaChapter(): Promise<void> {
        const focusAreaChapter = this.currentDocument.chapters.find((chapter) => chapter.isFocusArea);
        if (focusAreaChapter) await this.lockChapter(focusAreaChapter.id!);
    }

    public async unlockFocusAreaChapter(): Promise<void> {
        const focusAreaChapter = this.currentDocument.chapters.find((chapter) => chapter.isFocusArea);
        if (focusAreaChapter) await this.unlockChapter(focusAreaChapter.id!);
    }

    public async lockDecisionOptionChapter(): Promise<void> {
        if (this.evaDecisionOptionChapter) await this.lockChapter(this.evaDecisionOptionChapter.id!);
    }

    public async unlockDecisionOptionChapter(): Promise<void> {
        if (this.evaDecisionOptionChapter) await this.unlockChapter(this.evaDecisionOptionChapter.id!);
    }

    public async lockAllChapters(): Promise<void> {
        await this.lockEveryChapter();
    }

    public async changeDocumentIsOfficial(newIsOfficialState: boolean): Promise<void> {
        const documentWithNewIsOfficialState: DocumentDto = { ...this.currentDocument, isOfficial: newIsOfficialState };

        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + 'document/changeDocumentOfficialState',
            data: documentWithNewIsOfficialState,
            withCredentials: true,
        });
        runInAction(() => {
            if (response.status === 200) {
                this.currentDocument.isOfficial = response.data;
            }
        });
    }

    public async fetchOfficialDocuments(
        type: 'LARGE_REPORT' | 'YEARLY_REPORT' | null,
    ): Promise<ListOfficialDocumentsResponse> {
        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + `document/listOfficialDocuments`,
            data: { documentId: this.currentDocument.id, type: type },
            withCredentials: true,
        });
        return response.data;
    }

    public async lockPackage(packageId: number): Promise<void> {
        const response = await Axios({
            method: 'PUT',
            url: BACKEND_URL + `chapter/lockPackage/${packageId}`,
            withCredentials: true,
        });

        if (response.status === 200)
            this.updateChapterIndicatorPackageData(response.data as ChapterIndicatorPackageDto, true);
    }

    public async unlockPackage(packageId: number): Promise<void> {
        const response = await Axios({
            method: 'PUT',
            url: BACKEND_URL + `chapter/unlockPackage/${packageId}`,
            withCredentials: true,
        });
        runInAction(() => {
            if (response.status === 200) {
                const indicatorPackage = this.currentDocument.chapters
                    .find((chapter) => chapter.isIndicatorArea)
                    ?.indicatorPackages.find((pack) => pack.id === packageId);
                if (indicatorPackage) indicatorPackage.locked = false;
            }
        });
    }

    public async releasePackageLocks(): Promise<void> {
        const indicatorChapter = this.rootModule.document.currentDocument.chapters.find(
            (chapter) => chapter.isIndicatorArea,
        );
        const packagesLockedByUser = indicatorChapter?.indicatorPackages.filter((pkg) => pkg.locked) || [];
        const packageIds = packagesLockedByUser.map((pkg) => pkg.id!);

        await Axios({
            method: 'POST',
            url: BACKEND_URL + 'chapter/releasePackageLocks',
            data: { ids: packageIds },
        });

        runInAction(() =>
            this.rootModule.document.currentDocument.chapters.forEach((chp) => {
                if (chp.isIndicatorArea) {
                    chp.indicatorPackages.forEach((pkg) => {
                        pkg.locked = false;
                    });
                }
            }),
        );
    }

    public get isPackageLocked(): boolean {
        if (this.rootModule.document.currentDocument && this.rootModule.document.currentDocument.chapters) {
            // The current document is set and contains chapters, so check if there is indicator area chapter.
            const indicatorChapter = this.rootModule.document.currentDocument.chapters.find(
                (chp) => chp.isIndicatorArea,
            );
            // If indicator chapter exists, check if it has any indicator packages that are locked, otherwise return false.
            return indicatorChapter ? indicatorChapter.indicatorPackages.some((pkg) => pkg.locked) : false;
        } else {
            // Either the current document is not set, or it has no chapters — therefore, there can't be any locked indicator packages.
            return false;
        }
    }

    public async releaseChapterLocks(): Promise<void> {
        const chaptersLockedByUser = this.currentDocument.chapters.filter((chp) => chp.locked);
        const chapterIds = chaptersLockedByUser.map((chp) => chp.id);

        await Axios({
            method: 'POST',
            url: BACKEND_URL + 'chapter/releaseChapterLocks',
            data: { ids: chapterIds },
            withCredentials: true,
        });
        runInAction(() =>
            this.currentDocument.chapters.map((chp) => {
                if (chp.locked) {
                    chp.locked = false;
                    return chp;
                }
                return chp;
            }),
        );
    }

    public lockChapterById(id: number) {
        const chapter = this.currentDocument.chapters.find((chp) => chp.id === id);
        if (chapter) {
            chapter.locked = true;
        }
    }

    public unlockChapterById(id: number) {
        const chapter = this.currentDocument.chapters.find((chp) => chp.id === id);
        if (chapter) {
            chapter.locked = false;
        }
    }

    public updateChapterText(text: string, id: number): void {
        const chapter = this.activeDocument.chapters.find((chapter) => chapter.id === id);
        if (chapter) chapter.text = text;
    }

    public updateChapterTitle(title: string, id: number): void {
        const chapter = this.activeDocument.chapters.find((chapter) => chapter.id === id);
        if (chapter) chapter.title = title;
    }

    public updateChapterEditor(editor: string, id: number): void {
        const chapter = this.activeDocument.chapters.find((chapter) => chapter.id === id);
        if (chapter) chapter.editor = editor;
    }

    public updateChapterLastUpdated(updated: number, id: number): void {
        const chapter = this.activeDocument.chapters.find((chapter) => chapter.id === id);
        if (chapter) chapter.lastUpdated = updated;
    }

    public updateChapterVersion(version: number, id: number): void {
        const chapter = this.activeDocument.chapters.find((chapter) => chapter.id === id);
        if (chapter) chapter.version = version;
    }

    public updateChapterFocusAreas(focusAreas: FocusAreaDto[], id: number): void {
        const chapter = this.activeDocument.chapters.find((chapter) => chapter.id === id);
        if (chapter) chapter.focusAreas = focusAreas;
    }

    public updateChapterEvaDecisionOptions(evaDecisionOptions: EvaDecisionOptionDto[], id: number): void {
        const chapter = this.activeDocument.chapters.find((chapter) => chapter.id === id);
        if (chapter) chapter.evaDecisionOptions = evaDecisionOptions;
    }

    public updateChapterData(data: ChapterDto): void {
        this.updateChapterText(data.text || '', data.id!);
        this.updateChapterTitle(data.title || '', data.id!);
        this.updateChapterEditor(data.editor || '', data.id!);
        this.updateChapterLastUpdated(data.lastUpdated, data.id!);
        this.updateChapterVersion(data.version, data.id!);
        this.updateChapterFocusAreas(data.focusAreas, data.id!);
        this.updateChapterEvaDecisionOptions(data.evaDecisionOptions, data.id!);
    }

    public updateChapterIndicatorPackageData(data: ChapterIndicatorPackageDto, lockPackage = false): void {
        const indicatorChapter = this.indicatorPackageChapter;

        if (indicatorChapter) {
            const indicatorPackage = indicatorChapter.indicatorPackages.find((pkg) => pkg.id === data.id!);

            if (indicatorPackage) {
                indicatorPackage.notes = data.notes;
                indicatorPackage.editor = data.editor;
                indicatorPackage.version = data.version;
                indicatorPackage.lastUpdated = data.lastUpdated;
                if (lockPackage) indicatorPackage.locked = true;
            }
        }
    }

    public async saveChapter(chapter: ChapterDto) {
        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + 'chapter/saveChapter',
            data: chapter,
            withCredentials: true,
        });
        const savedChapter = response.data as ChapterDto;
        this.updateChapterText(savedChapter.text || '', savedChapter.id!);
        this.updateChapterTitle(savedChapter.title || '', savedChapter.id!);
        this.updateChapterEditor(savedChapter.editor || '', savedChapter.id!);
        this.updateChapterLastUpdated(savedChapter.lastUpdated, savedChapter.id!);
        this.updateChapterVersion(savedChapter.version, savedChapter.id!);
    }

    public async removeChapter(chapterId: number) {
        await Axios({
            method: 'DELETE',
            url: BACKEND_URL + `chapter/removeChapter/${chapterId}`,
            withCredentials: true,
        });
    }

    public async createChapter(
        documentId: number,
        orderNumber: number,
        chapterLevel: number,
        title?: string,
        text?: string,
    ): Promise<void> {
        const newChapter: ChapterDto = {
            id: null,
            version: 0,
            documentId: documentId,
            chapterLevel: chapterLevel,
            orderNumber: orderNumber,
            title: title ?? null,
            text: text ?? null,
            editor: null,
            lastUpdated: 0,
            indicatorPackages: [],
            focusAreas: [],
            evaDecisionOptions: [],
            locked: false,
            tip: null,
            isFocusArea: false,
            isApprovedArea: false,
            isIndicatorArea: false,
            isRemovable: false,
            isEvaActorSummary: false,
            isEditable: false,
            isTitleEditable: false,
            isVisible: false,
            isEvaDecisionOptionArea: false,
            isEvaPerspectiveArea: false,
            isEvaDecisionOptionsComparisonArea: false,
            isPublishable: false,
            isSummary: false,
            isIntroduction: false,
            isCompareToCountiesMainArea: false,
            isCompareToCountiesSubArea: false,
            isCompareToWellbeingCountyArea: false,
        };

        await Axios({
            method: 'POST',
            url: BACKEND_URL + `chapter/createChapter`,
            data: newChapter,
            withCredentials: true,
        });
    }

    public async downloadPowerPoint(params: DownloadPowerPointParams): Promise<void> {
        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + `document/${this.currentDocument.id}/documentExport/downloadPowerPoint`,
            data: { ...params },
            responseType: 'blob',
        });
        const blob = new Blob([response.data], {
            type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        });
        downloadjs(
            blob,
            `${this.currentDocument.name}.pptx`,
            'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        );
    }

    public async savePackage(indicatorPackage: ChapterIndicatorPackageDto) {
        const response = await Axios({
            method: 'POST',
            url:
                BACKEND_URL +
                `document/${this.currentDocument.id}/chapter/${indicatorPackage.chapterId}/chapterIndicator/updatePackage`,
            data: indicatorPackage,
            withCredentials: true,
        });

        if (response.status === 200)
            this.updateChapterIndicatorPackageData(response.data as ChapterIndicatorPackageDto);
    }

    public async uploadIndicator(file: File): Promise<IndicatorDto[]> {
        const form = new FormData();
        form.append('file', file);
        form.append('id', this.currentDocument.organization.id!.toString());

        const indicatorChapterId = this.rootModule.document.indicatorPackageChapter?.id;

        const response = await Axios({
            method: 'POST',
            url:
                BACKEND_URL +
                `document/${this.currentDocument.id}/chapter/${indicatorChapterId}/chapterOrganizationIndicator/uploadOrganizationIndicator`,
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            data: form,
        });
        if (response.status === 200) {
            return response.data;
        } else return [];
    }

    public get previousDocumentShares(): DocumentShareDto[] {
        return this.documentShares.slice().sort((a, b) => {
            return b.dateCreated - a.dateCreated;
        });
    }

    public get documentHistory(): DocumentHistoryDto[] {
        return this.currentDocumentHistory.slice().sort((a, b) => {
            if (a.dateCreated === null) return 1;
            if (b.dateCreated === null) return -1;
            return b.dateCreated - a.dateCreated;
        });
    }

    public get documentHistoryDates(): string[] {
        return this.currentDocumentHistoryDates;
    }

    public get documentTypes(): DocumentTypeDto[] {
        return this.docTypes;
    }

    public get documentStatuses(): DocumentStatus[] {
        return this.docStatuses;
    }

    public get latestPublishedDocuments(): DocumentDto[] {
        return this.latestPublishedDocs;
    }

    public get organizationShvkDocuments(): DocumentDto[] {
        return this.organizationDocs.filter(
            (d) =>
                d.documentType.code === 'YEARLY_REPORT' ||
                d.documentType.code === 'LARGE_REPORT' ||
                d.documentType.code === 'REGIONAL_YEARLY_REPORT' ||
                d.documentType.code === 'REGIONAL_LARGE_REPORT',
        );
    }

    public get organizationEvaDocuments(): DocumentDto[] {
        return this.organizationDocs.filter((d) => d.documentType.code === 'EVA' || d.documentType.code === 'EVA_MINI');
    }

    public get organizationAnotherDocuments(): DocumentDto[] {
        return this.organizationDocs.filter((d) => d.documentType.code === 'ANOTHER_REPORT');
    }

    public get isCurrentDocumentEva(): boolean {
        return this.isEvaDocument(this.currentDocument);
    }

    public isEvaDocument(doc: DocumentDto | string) {
        if (typeof doc === 'string') {
            return doc === 'EVA' || doc === 'EVA_MINI';
        } else {
            return doc.documentType.code === 'EVA' || doc.documentType.code === 'EVA_MINI';
        }
    }

    public getDocumentTypeByCode(code: string): DocumentTypeDto | null {
        const type = this.documentTypes.find((type) => type.code === code);
        return type || null;
    }

    public getDocumentStatusByCode(code: string): DocumentStatus | null {
        const status = this.documentStatuses.find((status) => status.code === code);
        return status || null;
    }

    public getOrganizationDocumentById(id: number): DocumentDto | null {
        const document = this.organizationDocs.find((doc) => doc.id === id);
        return document || null;
    }

    public get evaDecisionOptionChapter(): ChapterDto | null {
        const chapter = this.activeDocument.chapters.find((c) => c.isEvaDecisionOptionArea);
        return chapter || null;
    }

    public get indicatorPackageChapter(): ChapterDto | null {
        const chapter = this.activeDocument.chapters.find((c) => c.indicatorPackages.length > 0);
        return chapter || null;
    }

    private get nullDocument(): DocumentDto {
        return {
            acceptanceClause: null,
            acceptedBy: null,
            acceptedDate: null,
            chapters: [],
            coverPageImage: null,
            coverPageImageOrientation: null,
            coverPageText: null,
            creator: {
                id: 0,
                userName: '',
                organizationId: null,
                selectedOrganizationId: null,
                accountExpired: false,
                accountLocked: false,
                enabled: false,
                firstName: '',
                lastName: '',
                passwordExpired: false,
                profession: '',
                removed: false,
                unitOrBranch: null,
            },
            dateCreated: null,
            directoryId: 0,
            id: 0,
            language: 'fi_FI',
            name: '',
            isOfficial: false,
            isPublished: false,
            removed: false,
            isPermitted: false,
            userBelongsToWorkgroup: false,
            journalNumber: null,
            organization: {
                active: false,
                category: '',
                code: '',
                id: 0,
                latitude: 0,
                longitude: 0,
                uri: '',
                name: {
                    id: 0,
                    key: '',
                    fi_FI: '',
                    sv_SE: '',
                    en_GB: '',
                },
                shortName: {
                    id: 0,
                    key: '',
                    fi_FI: '',
                    sv_SE: '',
                    en_GB: '',
                },
                isCollectionCategory: false,
                isRegional: false,
                isPartOfRegional: false,
            },
            documentOwner: {
                id: 0,
                userName: '',
                organizationId: null,
                selectedOrganizationId: null,
                accountExpired: false,
                accountLocked: false,
                enabled: false,
                firstName: '',
                lastName: '',
                passwordExpired: false,
                profession: '',
                removed: false,
                unitOrBranch: null,
            },
            sharedData: '',
            documentStatus: {
                id: 0,
                code: 'STATUS_COMPLETE',
            },
            documentType: {
                id: 0,
                code: 'LARGE_REPORT',
                description: {
                    id: 0,
                    en_GB: '',
                    fi_FI: '',
                    key: '',
                    sv_SE: '',
                },
                name: {
                    id: 0,
                    en_GB: '',
                    fi_FI: '',
                    key: '',
                    sv_SE: '',
                },
                orderNumber: 0,
            },
            users: [],
            yearBegin: null,
            yearEnd: null,
            indicatorSetting: {
                changeYearsVisibility: 0,
                compareDirection: 0,
                documentId: 0,
                graphScale: 0,
                id: 0,
                organizations: [],
                startYear: 0,
                endYear: 0,
            },
            focusAreaTableTitles: {
                documentId: 0,
                actionTitle: '',
                additionalColumnByActionTitle: '',
                additionalColumnByGoalTitle: '',
                evaluationGaugeTitle: '',
                resourceTitle: '',
            },
        };
    }

    public async closeCurrentDocument(): Promise<void> {
        this.activeDocument.id && !this.isPreview && (await this.releaseChapterLocks());
        runInAction(() => (this.rootModule.indicator.summaryData = null));
        runInAction(() => (this.activeDocument = this.nullDocument));
    }

    public get isCurrentDocumentApproved(): boolean {
        return this.activeDocument.documentStatus.code === 'STATUS_COMPLETE';
    }

    public get isCurrentDocumentEvaMini(): boolean {
        return this.activeDocument.documentType.code === 'EVA_MINI';
    }

    public get isAnotherReport(): boolean {
        return this.activeDocument.documentType.code === 'ANOTHER_REPORT';
    }

    public get hasSummary(): boolean {
        return this.activeDocument.chapters.some((chapter) => chapter.isSummary);
    }

    public get isLargeReport(): boolean {
        return (
            this.activeDocument.documentType.code === 'LARGE_REPORT' ||
            this.activeDocument.documentType.code === 'REGIONAL_LARGE_REPORT'
        );
    }

    public get hasUserRightsToCurrentDocument(): boolean {
        return this.isUserCurrentDocumentOrganizationAdmin || this.isUserCurrentDocumentCreatorOrOwner;
    }

    public get isUserCurrentDocumentOrganizationAdmin(): boolean {
        const organizationUser = this.rootModule.user.userDetails?.organizations.find(
            (orgUser) => orgUser.organization.id === this.currentDocument.organization.id,
        );
        return organizationUser?.isAdmin || false;
    }

    public get isUserCurrentDocumentCreatorOrOwner(): boolean {
        const userId = this.rootModule.user.userDetails?.user.id;
        return this.currentDocument.documentOwner.id === userId || this.currentDocument.creator.id === userId;
    }

    public async loadDocumentById(id: number): Promise<void> {
        // Identify chapters with the text editor open to keep them in editing mode after the document loads.
        const chaptersOnEditingMode: number[] = [];
        const indicatorPackagesOnEditingMode: number[] = [];
        if (this.currentDocument && this.currentDocument.chapters) {
            this.currentDocument.chapters.forEach((chapter) => {
                if (chapter.locked) {
                    chaptersOnEditingMode.push(chapter.id!);
                }
                if (chapter.isIndicatorArea) {
                    chapter.indicatorPackages.forEach((pkg) => {
                        if (pkg.locked) {
                            indicatorPackagesOnEditingMode.push(pkg.id!);
                        }
                    });
                }
            });
        }
        const response = await Axios({
            method: 'GET',
            url: BACKEND_URL + `document/loadById/${id}`,
            withCredentials: true,
        });
        this.setCurrentDocument(response.data);
        runInAction(() => {
            // Manually set chapters unlocked when opening a document.
            // This makes sure chapter editors are closed by default,
            // even if the same document was open in another browser tab earlier.
            this.currentDocument.chapters = this.currentDocument.chapters.map((chp) => {
                if (!chaptersOnEditingMode.includes(chp.id!)) {
                    chp.locked = false;
                }

                if (chp.isIndicatorArea) {
                    chp.indicatorPackages = chp.indicatorPackages.map((pkg) => {
                        if (!indicatorPackagesOnEditingMode.includes(pkg.id!)) {
                            pkg.locked = false;
                        }
                        return pkg;
                    });
                }
                return chp;
            });
        });
    }

    public async loadForPreviewById(id: number): Promise<void> {
        const response = await Axios({
            method: 'GET',
            url: BACKEND_URL + `document/loadPreviewById/${id}`,
            withCredentials: true,
        });
        this.setCurrentDocument(response.data as DocumentDto);
    }

    public async loadPublishedDocumentById(id: number): Promise<void> {
        const response = await Axios({
            method: 'GET',
            url: BACKEND_URL + `document/loadPublishedById/${id}`,
            withCredentials: true,
        });
        this.setCurrentDocument(response.data);
    }

    private get createEvaDecisionOptionCompareTable(): any[] {
        const { translate } = this.rootModule.localization;
        const shortTerm = translate('EVA_TERM_EFFECT_PART_1_SHORT') + ' ' + translate('EVA_TERM_EFFECT_PART_3');
        const longTerm = translate('EVA_TERM_EFFECT_PART_2_LONG') + ' ' + translate('EVA_TERM_EFFECT_PART_3');
        const description = translate('EVA_DECISION_OPTION_DESCRIPTION');
        const evaDecisionOptions = this.currentDocument.chapters.find(
            (chapter) => chapter.isEvaDecisionOptionArea,
        )!.evaDecisionOptions;
        const perspectives = evaDecisionOptions[0].evaPerspectives;

        //First create empty table
        const evaDecisionOptionCompareTable: any[] = [];

        perspectives.forEach((perspective, perspectiveIndex) => {
            // Add perspective title
            evaDecisionOptionCompareTable.push([
                {
                    clickable: false,
                    title: perspective.title,
                    type: 'perspectiveTitle',
                },
                ...evaDecisionOptions.map(() => {
                    return {
                        clickable: false,
                        field: 'blank',
                    };
                }),
            ]);
            perspective.impactTargets.forEach((impactTarget, impactIndex) => {
                // Add impact of target title row
                evaDecisionOptionCompareTable.push([
                    {
                        clickable: false,
                        field: 'impact',
                        impactOnTarget: impactTarget,
                        text: impactTarget.target,
                    },
                    ...evaDecisionOptions.map((decisionOption) => {
                        return {
                            clickable: true,
                            field: 'blank',
                            impactOnTarget: decisionOption.evaPerspectives[perspectiveIndex].impactTargets[impactIndex],
                            option: decisionOption,
                            perspectiveId: decisionOption.evaPerspectives[perspectiveIndex].id,
                            text: decisionOption.evaPerspectives[perspectiveIndex].impactTargets[impactIndex]
                                .description,
                        };
                    }),
                ]);
                // Add short term value row
                evaDecisionOptionCompareTable.push([
                    {
                        text: shortTerm,
                        type: 'tableHeading',
                        clickable: false,
                        impactOnTarget: impactTarget,
                    },
                    ...evaDecisionOptions.map((decisionOption) => {
                        return {
                            clickable: true,
                            impactOnTarget: decisionOption.evaPerspectives[perspectiveIndex].impactTargets[impactIndex],
                            option: decisionOption,
                            perspectiveId: decisionOption.evaPerspectives[perspectiveIndex].id,
                            shortTermEffect:
                                decisionOption.evaPerspectives[perspectiveIndex].impactTargets[impactIndex]
                                    .shortTermEffect,
                            type: 'shortTermValue',
                        };
                    }),
                ]);
                // Add long term value row
                evaDecisionOptionCompareTable.push([
                    {
                        clickable: false,
                        impactOnTarget: impactTarget,
                        text: longTerm,
                        type: 'tableHeading',
                    },
                    ...evaDecisionOptions.map((decisionOption) => {
                        return {
                            clickable: true,
                            impactOnTarget: decisionOption.evaPerspectives[perspectiveIndex].impactTargets[impactIndex],
                            longTermEffect:
                                decisionOption.evaPerspectives[perspectiveIndex].impactTargets[impactIndex]
                                    .longTermEffect,
                            option: decisionOption,
                            perspectiveId: decisionOption.evaPerspectives[perspectiveIndex].id,
                            type: 'longTermValue',
                        };
                    }),
                ]);
                // Add comments row
                evaDecisionOptionCompareTable.push([
                    {
                        clickable: false,
                        impactOnTarget: impactTarget,
                        text: description,
                        type: 'tableHeading',
                    },
                    ...evaDecisionOptions.map((decisionOption) => {
                        return {
                            clickable: true,
                            field: 'dop',
                            impactOnTarget: decisionOption.evaPerspectives[perspectiveIndex].impactTargets[impactIndex],
                            option: decisionOption,
                            perspectiveId: decisionOption.evaPerspectives[perspectiveIndex].id,
                            text: decisionOption.evaPerspectives[perspectiveIndex].impactTargets[impactIndex]
                                .description,
                        };
                    }),
                ]);
            });
        });

        return [evaDecisionOptionCompareTable];
    }

    public get currentDocumentIndicators(): IndicatorDto[] {
        const indicators: IndicatorDto[] = [];
        this.indicatorPackageChapter?.indicatorPackages.forEach((indicatorPackage: ChapterIndicatorPackageDto) =>
            indicatorPackage.themes.forEach((indicatorPackageTheme: ChapterIndicatorThemeDto) => {
                indicatorPackageTheme.indicators.forEach((indicator: IndicatorDto) => {
                    indicators.push(indicator);
                });
            }),
        );
        return indicators;
    }

    public findIndicatorById(id: number): IndicatorDto | null {
        let foundIndicator: IndicatorDto | null = null;
        this.indicatorPackageChapter?.indicatorPackages.forEach((indicatorPackage: ChapterIndicatorPackageDto) =>
            indicatorPackage.themes.forEach((indicatorPackageTheme: ChapterIndicatorThemeDto) => {
                indicatorPackageTheme.indicators.forEach((indicator: IndicatorDto) => {
                    if (indicator.id === id) foundIndicator = indicator;
                });
            }),
        );
        return foundIndicator;
    }

    public setIndicatorPackageThemes(themes: ChapterIndicatorThemeDto[], indicatorPackageId: number): void {
        this.activeDocument.chapters = this.activeDocument.chapters.map((chapter) => {
            if (chapter.indicatorPackages.length === 0) return chapter;

            chapter.indicatorPackages = chapter.indicatorPackages.map((indicatorPackage) => {
                if (indicatorPackage.id !== indicatorPackageId) return indicatorPackage;

                indicatorPackage.themes = themes;
                return indicatorPackage;
            });
            return chapter;
        });
    }

    private setCurrentDocument(document: DocumentDto): void {
        if (document.language !== this.rootModule.localization.locale) {
            this.rootModule.dialog.openLanguageChangedDialog();

            const newLanguage = this.rootModule.localization.languages.find(
                (language) => language.locale === document.language,
            );
            if (newLanguage) this.rootModule.localization.changeLanguage(newLanguage);
        }
        this.activeDocument = document;

        // Eva documents doesn't have comparisonSettings
        if (this.isCurrentDocumentEva) {
            this.rootModule.indicator.clearComparisonSettings();
            return;
        }

        if (this.activeDocument.indicatorSetting) {
            this.rootModule.indicator.setComparisonSettings(this.activeDocument.indicatorSetting);
        }
        // todo handleShvkToolLanguageByDocumentLanguage
    }

    public getChaptersWithNumbers = (): ChapterWithNumber[] => {
        let level1Number = 1;
        let level2Number = 1;
        let level3Number = 1;

        const orderedChapters = this.activeDocument.chapters.slice().sort((a, b) => a.orderNumber - b.orderNumber);

        return orderedChapters.map((chapter) => {
            if (chapter.chapterLevel === 0) {
                // No numbers on level 0 title.
                level2Number = 1;
                level3Number = 1;
                return { chapter: chapter, chapterNumber: '' };
            }

            if (chapter.chapterLevel === 1) {
                const number = `${level1Number}`;
                level1Number++;
                level2Number = 1;
                level3Number = 1;
                return { chapter: chapter, chapterNumber: number };
            }

            if (chapter.chapterLevel === 2) {
                const number = `${level1Number - 1}.${level2Number}`;
                level2Number++;
                level3Number = 1;
                return { chapter: chapter, chapterNumber: number };
            }

            if (chapter.chapterLevel === 3) {
                const number = `${level1Number - 1}.${level2Number - 1}.${level3Number}`;
                level3Number++;
                return { chapter: chapter, chapterNumber: number };
            }

            // This should not happen!
            return { chapter: chapter, chapterNumber: '' };
        });
    };
}
